import { Cart, CartItem } from '../types/cart'
import { ShoppingCart, ShoppingCartItem, CreateOrderData, OrderType, PaymentMethod, OrderLineRelationshipType } from '../types/order'

export class CartTransformationService {
  // Helper function to map cart item types to relationship types
  private static mapCartTypeToRelationshipType(cartItem: CartItem): OrderLineRelationshipType {
    switch (cartItem.type) {
      case 'main':
        return 'MAIN_ITEM'
      case 'bundle':
        return 'BUNDLE_UPGRADE'
      case 'meal_part':
        return 'INCLUDED_PART'
      case 'extra':
        return 'CUSTOMIZATION'
      default:
        return 'MAIN_ITEM'
    }
  }

  // Helper function to generate relationship group ID
  private static generateRelationshipGroupId(cartItem: CartItem, mainItemId?: string): string {
    // Prefer explicit group id if present on the cart item
    if (cartItem.relationship_group_id && cartItem.relationship_group_id.trim().length > 0) {
      return cartItem.relationship_group_id
    }

    // For main items, use the cart item's unique id so each order line gets its own group
    if (cartItem.type === 'main' && cartItem.id) {
      return `group-${cartItem.id}`
    }

    // For children, bundle_parent_id is the parent main item's id
    if (cartItem.bundle_parent_id) {
      return `group-${cartItem.bundle_parent_id}`
    }

    // Fallback
    const resolvedMainId = (typeof mainItemId === 'string' && mainItemId) || String(cartItem.product_variant_id)
    return `group-main-${resolvedMainId}`
  }

  // Helper function to calculate sort order based on item type
  private static calculateSortOrder(cartItem: CartItem): number {
    switch (cartItem.type) {
      case 'main':
        return 1
      case 'bundle':
        return 2
      case 'meal_part':
        return 3
      case 'extra':
        return 4
      default:
        return 5
    }
  }

  /**
   * Statiegeld for the whole cart: Σ(deposit per unit x quantity).
   *
   * Kept separate from cart.total_cents on purpose, mirroring the API: the
   * deposit carries no BTW and is never discountable, so it must not enter the
   * goods total that the discount preview is calculated on. It is added back
   * once, at the very end, when building the total we send and the total we
   * show. Bundle upgrade lines are excluded — they are a price delta on a
   * variant that is already counted by its own line, so counting them would
   * charge the same can's deposit twice (the API skips them for this reason).
   */
  static calculateCartDepositCents(cart: Cart): number {
    return cart.items.reduce((sum, item) => {
      if (item.type === 'bundle') return sum
      return sum + (item.deposit_cents || 0) * item.quantity
    }, 0)
  }

  static transformCartToOrderData(
    cart: Cart,
    paymentMethod: PaymentMethod,
    customerName?: string,
    orderType: OrderType = 'DINE_IN',
    notes?: string,
    staffUserId: number = 1,
    terminalId?: string | null,
    couponCode?: string,
    consumingEmployeeId?: number,
    /**
     * Verpakkingskosten for THIS order type, already resolved by the caller
     * (useOrderCharges.packagingFeeCentsFor) — 0 for dine-in. Like statiegeld
     * it is never discounted and never part of the goods total; unlike
     * statiegeld it is taxed, but that split is the API's business, not ours.
     */
    packagingFeeCents: number = 0
  ): CreateOrderData {
    // Calculate totals with proper tax handling and subcharges
    const taxRatePercentage = 9.0 // Default tax rate
    let subtotalCents = 0
    let totalTaxCents = 0
    
    cart.items.forEach(item => {
      // Calculate subcharges for this item
      const customizations = item.selected_options?.length > 0 ? {
        selected_options: item.selected_options.map(option => ({
          option_group_id: option.option_group_id,
          option_item_id: option.option_item_id,
          price_delta_cents: option.price_delta_cents
        }))
      } : undefined
      
      const subchargesTotalCents = this.calculateSubcharges(customizations)
      
      // Use robust tax calculation approach
      const taxInclusivePriceCents = item.price_cents
      const surchargesTaxInclusiveCents = Math.round(subchargesTotalCents * (1 + taxRatePercentage / 100))
      
      // Calculate total tax-inclusive amount first
      const totalTaxInclusiveCents = (taxInclusivePriceCents + surchargesTaxInclusiveCents) * item.quantity
      
      // Convert total to tax-exclusive in one operation
      const totalTaxExclusiveCents = Math.round(totalTaxInclusiveCents / (1 + taxRatePercentage / 100))
      const itemTaxCents = totalTaxInclusiveCents - totalTaxExclusiveCents
      
      subtotalCents += totalTaxExclusiveCents
      totalTaxCents += itemTaxCents
    })

    // Goods only — this is what the discount engine prices.
    const goodsTotalCents = subtotalCents + totalTaxCents
    // Statiegeld rides on top: outside BTW, never discounted. The API derives
    // it from product_variant.deposit_cents and adds it the same way, and
    // rejects the order (>2c) if our total disagrees.
    const depositTotalCents = this.calculateCartDepositCents(cart)
    const totalCents = goodsTotalCents + depositTotalCents + packagingFeeCents

    // Transform cart items to order lines
    const orderLines = cart.items.map(item => this.transformCartItemToOrderLine(item))

    // Payment is now required - always include it
    // Staff meal orders are always €0 (100% discount applied server-side)
    const payment: CreateOrderData['payment'] = {
      payment_method: paymentMethod,
      amount_cents: paymentMethod === 'STAFF_MEAL' ? 0 : totalCents
    }

    // Add CCV fields for card payments
    if (paymentMethod === 'CARD') {
      payment.provider = 'ccv'
      // Use provided terminal_id if set
      // Empty string means user explicitly selected "None" - throw error (terminal required for card payments)
      // null/undefined means not selected yet - use fallback for backward compatibility
      if (terminalId && terminalId !== '') {
        payment.terminal_id = terminalId
      } else if (terminalId === '') {
        // User explicitly selected "None" - terminal is required for card payments
        throw new Error('Payment terminal is required for card payments. Please select a terminal and try again.')
      } else if (terminalId === null || terminalId === undefined) {
        // Not selected yet, use fallback for backward compatibility
        payment.terminal_id = 'LEEN0208'
      }
      payment.operating_environment = 'ATTENDED'
      payment.access_protocol = 'OPI_NL'
    }

    const orderData: CreateOrderData = {
      customer_name: customerName,
      order_type: orderType,
      staff_user_id: staffUserId,
      consuming_employee_id: consumingEmployeeId,
      subtotal_cents: subtotalCents,
      total_cents: totalCents,
      notes,
      coupon_code: couponCode || undefined,
      order_lines: orderLines,
      payment
    }

    return orderData
  }

  static transformCartToShoppingCart(
    cart: Cart,
    customerName?: string,
    orderType: OrderType = 'DINE_IN',
    notes?: string,
    staffUserId: number = 1
  ): ShoppingCart {
    // Calculate totals with proper tax handling and subcharges
    const taxRatePercentage = 9.0 // Default tax rate
    let subtotalCents = 0
    let totalTaxCents = 0
    
    cart.items.forEach(item => {
      // Calculate subcharges for this item
      const customizations = item.selected_options?.length > 0 ? {
        selected_options: item.selected_options.map(option => ({
          option_group_id: option.option_group_id,
          option_item_id: option.option_item_id,
          price_delta_cents: option.price_delta_cents
        }))
      } : undefined
      
      const subchargesTotalCents = this.calculateSubcharges(customizations)
      
      // Use robust tax calculation approach
      const taxInclusivePriceCents = item.price_cents
      const surchargesTaxInclusiveCents = Math.round(subchargesTotalCents * (1 + taxRatePercentage / 100))
      
      // Calculate total tax-inclusive amount first
      const totalTaxInclusiveCents = (taxInclusivePriceCents + surchargesTaxInclusiveCents) * item.quantity
      
      // Convert total to tax-exclusive in one operation
      const totalTaxExclusiveCents = Math.round(totalTaxInclusiveCents / (1 + taxRatePercentage / 100))
      const itemTaxCents = totalTaxInclusiveCents - totalTaxExclusiveCents
      
      subtotalCents += totalTaxExclusiveCents
      totalTaxCents += itemTaxCents
    })

    const totalCents = subtotalCents + totalTaxCents

    // Transform cart items to shopping cart items
    const items: ShoppingCartItem[] = cart.items.map(item => this.transformCartItemToShoppingCartItem(item))

    return {
      customer_name: customerName,
      order_type: orderType,
      staff_user_id: staffUserId,
      subtotal_cents: subtotalCents,
      total_cents: totalCents,
      notes,
      items
    }
  }

  // Helper function to calculate subcharges from customizations (tax-exclusive)
  private static calculateSubcharges(customizations: any): number {
    if (!customizations?.selected_options) return 0;
    
    const taxRatePercentage = 9.0 // Default tax rate
    
    return customizations.selected_options.reduce((total: number, option: any) => {
      const taxInclusiveDelta = option.price_delta_cents || 0;
      // Convert tax-inclusive surcharge to tax-exclusive
      const taxExclusiveDelta = Math.round(taxInclusiveDelta / (1 + taxRatePercentage / 100));
      return total + taxExclusiveDelta;
    }, 0);
  }

  private static transformCartItemToOrderLine(cartItem: CartItem) {
    // Transform selected options to the API format
    const customizations = cartItem.selected_options?.length > 0 ? {
      selected_options: cartItem.selected_options.map(option => ({
        option_group_id: option.option_group_id,
        option_item_id: option.option_item_id,
        price_delta_cents: option.price_delta_cents
      }))
    } : undefined

    // Calculate subcharges from customizations
    const subchargesTotalCents = this.calculateSubcharges(customizations)

    // Calculate tax using robust approach to avoid rounding errors
    const taxRatePercentage = 9.0 // Default tax rate
    
    // Get original tax-inclusive amounts
    const taxInclusivePriceCents = cartItem.price_cents
    
    // Calculate surcharges tax-inclusive (convert back from tax-exclusive)
    const surchargesTaxInclusiveCents = Math.round(subchargesTotalCents * (1 + taxRatePercentage / 100))
    
    // Calculate total tax-inclusive amount first (avoiding multiple rounding)
    const totalTaxInclusiveCents = (taxInclusivePriceCents + surchargesTaxInclusiveCents) * cartItem.quantity
    
    // Convert total to tax-exclusive in one operation
    const totalTaxExclusiveCents = Math.round(totalTaxInclusiveCents / (1 + taxRatePercentage / 100))
    const taxAmountCents = totalTaxInclusiveCents - totalTaxExclusiveCents
    
    // For API, we need to provide unit_price and surcharges separately
    // unit_price_cents must be VAT-inclusive: the API back-calculates net from the inclusive price
    const taxExclusivePriceCents = Math.round(taxInclusivePriceCents / (1 + taxRatePercentage / 100))
    const subtotalCents = (taxExclusivePriceCents + subchargesTotalCents) * cartItem.quantity
    const totalCents = totalTaxInclusiveCents

    // Generate relationship group ID
    const relationshipGroupId = cartItem.relationship_group_id || this.generateRelationshipGroupId(cartItem)

    // Calculate sort order based on item type
    const sortOrder = this.calculateSortOrder(cartItem)

    return {
      product_variant_id: cartItem.product_variant_id,
      quantity: cartItem.quantity,
      unit_price_cents: taxInclusivePriceCents, // Base product price (VAT-inclusive, as expected by API)
      surcharges_total_cents: subchargesTotalCents, // NEW: Sum of all customization price deltas
      subtotal_cents: subtotalCents, // unit_price + subcharges (per unit) * quantity
      tax_rate_percentage: taxRatePercentage,
      tax_amount_cents: taxAmountCents,
      total_cents: totalCents,
      customizations_json: customizations,
      notes: undefined, // Per-line notes intentionally not captured; order-level note only
      // Relationship fields
      relationship_type: cartItem.relationship_type || this.mapCartTypeToRelationshipType(cartItem),
      relationship_group_id: relationshipGroupId,
      sort_order: cartItem.sort_order || sortOrder,
      bundle_group_id: cartItem.bundle_group_id
    }
  }

  private static transformCartItemToShoppingCartItem(cartItem: CartItem): ShoppingCartItem {
    // Transform selected options to the API format
    const customizations = cartItem.selected_options?.length > 0 ? {
      selected_options: cartItem.selected_options.map(option => ({
        option_group_id: option.option_group_id,
        option_item_id: option.option_item_id,
        price_delta_cents: option.price_delta_cents
      }))
    } : undefined

    // Calculate subcharges from customizations
    const subchargesTotalCents = this.calculateSubcharges(customizations)

    // Calculate tax using robust approach to avoid rounding errors
    const taxRatePercentage = 9.0 // Default tax rate
    
    // Get original tax-inclusive amounts
    const taxInclusivePriceCents = cartItem.price_cents
    
    // Calculate surcharges tax-inclusive (convert back from tax-exclusive)
    const surchargesTaxInclusiveCents = Math.round(subchargesTotalCents * (1 + taxRatePercentage / 100))
    
    // Calculate total tax-inclusive amount first (avoiding multiple rounding)
    const totalTaxInclusiveCents = (taxInclusivePriceCents + surchargesTaxInclusiveCents) * cartItem.quantity
    
    // Convert total to tax-exclusive in one operation
    const totalTaxExclusiveCents = Math.round(totalTaxInclusiveCents / (1 + taxRatePercentage / 100))
    const taxAmountCents = totalTaxInclusiveCents - totalTaxExclusiveCents
    
    // For API, we need to provide unit_price and surcharges separately
    // unit_price_cents must be VAT-inclusive: the API back-calculates net from the inclusive price
    const taxExclusivePriceCents = Math.round(taxInclusivePriceCents / (1 + taxRatePercentage / 100))
    const subtotalCents = (taxExclusivePriceCents + subchargesTotalCents) * cartItem.quantity
    const totalCents = totalTaxInclusiveCents

    // Generate relationship group ID
    const relationshipGroupId = cartItem.relationship_group_id || this.generateRelationshipGroupId(cartItem)

    // Calculate sort order based on item type
    const sortOrder = this.calculateSortOrder(cartItem)

    return {
      product_variant_id: cartItem.product_variant_id,
      quantity: cartItem.quantity,
      unit_price_cents: taxInclusivePriceCents, // Base product price (VAT-inclusive, as expected by API)
      subtotal_cents: subtotalCents,
      tax_rate_percentage: taxRatePercentage,
      tax_amount_cents: taxAmountCents,
      total_cents: totalCents,
      customizations_json: customizations,
      notes: undefined, // Per-line notes intentionally not captured; order-level note only
      // Relationship fields
      relationship_type: cartItem.relationship_type || this.mapCartTypeToRelationshipType(cartItem),
      relationship_group_id: relationshipGroupId,
      sort_order: cartItem.sort_order || sortOrder
    }
  }

  static calculateItemTotal(item: ShoppingCartItem): number {
    const basePrice = item.unit_price_cents
    const optionPrices = item.customizations_json?.selected_options?.reduce(
      (sum, opt) => sum + opt.price_delta_cents, 0
    ) || 0
    const bundlePrices = item.customizations_json?.bundle_selections?.reduce(
      (sum, bundle) => sum + bundle.price_delta_cents, 0
    ) || 0
    
    // Calculate tax-exclusive subtotal
    const subtotal = (basePrice + optionPrices + bundlePrices) * item.quantity
    // Calculate tax amount on the subtotal
    const taxAmount = Math.round(subtotal * (item.tax_rate_percentage / 100))
    
    return subtotal + taxAmount
  }

  static calculateCartTotal(cart: ShoppingCart): number {
    return cart.items.reduce((sum, item) => sum + item.total_cents, 0)
  }
}
